import { motion } from "framer-motion";
import { Syringe, CalendarCheck, TrendingDown, Bell, Sparkles } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

// Mock 6-week weight trend (kg) for chart
const weightData = [88.4, 87.6, 86.9, 85.8, 84.9, 84.1];
const doseWeek = [
  { d: "M", done: true },
  { d: "T", done: false },
  { d: "W", done: true, active: true },
  { d: "T", done: false },
  { d: "F", done: false },
  { d: "S", done: false },
  { d: "S", done: false },
];

function WeightChart() {
  const max = Math.max(...weightData);
  const min = Math.min(...weightData);
  const w = 260;
  const h = 90;
  const pad = 8;
  const step = (w - pad * 2) / (weightData.length - 1);
  const range = max - min || 1;
  const pts = weightData.map((v, i) => {
    const x = pad + i * step;
    const y = pad + ((max - v) / range) * (h - pad * 2);
    return [x, y];
  });
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`).join(" ");
  const areaPath = `${path} L ${pts[pts.length - 1][0]} ${h - pad} L ${pts[0][0]} ${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24">
      <defs>
        <linearGradient id="glp1grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#FF6B00" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#FF6B00" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#glp1grad)" />
      <path d={path} fill="none" stroke="#FF6B00" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([x, y], i) => (
        <circle
          key={i}
          cx={x}
          cy={y}
          r={i === pts.length - 1 ? 3.5 : 2}
          fill={i === pts.length - 1 ? "#FF6B00" : "#fff"}
          stroke="#FF6B00"
          strokeWidth="1.5"
        />
      ))}
    </svg>
  );
}

export default function Glp1Tracker() {
  return (
    <section
      id="glp1"
      className="py-20 md:py-24 bg-zoku-bg relative overflow-hidden"
      data-testid="glp1-section"
    >
      {/* Soft glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 -left-32 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-zoku-orangeLight blur-3xl opacity-60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Visual (LEFT on desktop) */}
        <motion.div {...reveal} className="lg:col-span-7 order-2 lg:order-1">
          <div className="relative">
            {/* Ambient blur behind card */}
            <div className="absolute -inset-8 rounded-[3rem] bg-white/40 blur-2xl pointer-events-none" />

            <div className="relative rounded-[2rem] bg-white border border-stone-200 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.25)] p-6 md:p-8">
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-zoku-orange/10 flex items-center justify-center">
                    <Syringe className="w-4.5 h-4.5 text-zoku-orange" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zoku-ink3 font-semibold">GLP-1 Tracker</div>
                    <div className="text-sm font-semibold text-zoku-ink">Week 6 · Semaglutide</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> On schedule
                </span>
              </div>

              {/* Body grid */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Left column — dose + week strip */}
                <div className="rounded-2xl bg-zoku-bg border border-stone-200 p-5">
                  <div className="text-[10px] uppercase tracking-widest text-zoku-ink3 font-semibold">Current dose</div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <div className="font-heading text-4xl font-bold text-zoku-ink leading-none">0.5</div>
                    <div className="text-sm font-medium text-zoku-ink2 pb-1">mg / week</div>
                  </div>
                  <div className="mt-1 text-xs text-zoku-ink3">Titrated up from 0.25 mg on Week 4</div>

                  <div className="mt-5 flex items-center justify-between">
                    {doseWeek.map((day, i) => (
                      <div key={i} className="flex flex-col items-center gap-1.5">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border transition ${
                            day.done
                              ? day.active
                                ? "bg-zoku-orange text-white border-zoku-orange shadow-md shadow-zoku-orange/30"
                                : "bg-zoku-orange/20 text-zoku-orange border-zoku-orange/40"
                              : "bg-white text-zoku-ink3 border-stone-200"
                          }`}
                        >
                          {day.done ? "✓" : ""}
                        </div>
                        <div className="text-[10px] text-zoku-ink3 font-medium">{day.d}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 flex items-center gap-2 text-xs text-zoku-ink2">
                    <Bell className="w-3.5 h-3.5 text-zoku-orange" />
                    <span>Next dose · <span className="font-semibold text-zoku-ink">Wed, 9:00 AM</span></span>
                  </div>
                </div>

                {/* Right column — weight trend */}
                <div className="rounded-2xl bg-gradient-to-br from-zoku-ink to-stone-800 p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Weight trend</div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300">
                      <TrendingDown className="w-3 h-3" /> −4.3 kg
                    </span>
                  </div>
                  <div className="mt-2 flex items-baseline gap-1.5">
                    <div className="font-heading text-3xl font-bold leading-none">84.1</div>
                    <div className="text-xs text-white/60">kg today</div>
                  </div>
                  <div className="mt-3">
                    <WeightChart />
                  </div>
                  <div className="mt-1 flex justify-between text-[10px] text-white/40 font-mono">
                    <span>W1</span><span>W6</span>
                  </div>
                </div>
              </div>

              {/* Bottom insight row */}
              <div className="mt-5 rounded-2xl bg-zoku-orange/8 border border-zoku-orange/20 p-4 flex items-start gap-3" style={{ background: "rgba(255,107,0,0.06)" }}>
                <div className="w-8 h-8 rounded-lg bg-zoku-orange/15 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-zoku-orange" />
                </div>
                <div className="text-sm text-zoku-ink2 leading-relaxed">
                  <span className="font-semibold text-zoku-ink">Zoku noticed</span> — your nausea logs dropped 60% this week. Hydration is up 1.4L/day. Keep it going.
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Copy (RIGHT on desktop) */}
        <motion.div {...reveal} className="lg:col-span-5 order-1 lg:order-2">
          <span className="inline-flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-1.5 text-xs font-semibold text-zoku-ink2">
            <Syringe className="w-3.5 h-3.5 text-zoku-orange" />
            GLP-1 companion
          </span>
          <h2 className="mt-5 font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-zoku-ink">
            Track your GLP-1 <br />
            <span className="font-editorial italic text-zoku-orange">effortlessly.</span>
          </h2>
          <p className="mt-5 text-lg text-zoku-ink2 max-w-lg">
            Log every dose in seconds, watch weight and side-effects trend in one place, and get gentle
            nudges before your next injection. Built for Semaglutide, Tirzepatide and Liraglutide protocols.
          </p>

          <div className="mt-8 space-y-4">
            {[
              { Icon: CalendarCheck, bg: "bg-emerald-100", iconC: "text-emerald-600", t: "Weekly dose reminders", d: "Never miss a shot. Titration schedule adapts to your protocol." },
              { Icon: TrendingDown, bg: "bg-amber-100", iconC: "text-amber-600", t: "Weight & side-effect logs", d: "Chart progress week over week. Spot patterns Zoku will explain." },
              { Icon: Sparkles, bg: "bg-rose-100", iconC: "text-rose-600", t: "AI-guided insights", d: "Personalized tips for nausea, hydration, protein, and workouts." },
            ].map((r, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-xl ${r.bg} flex items-center justify-center shrink-0`}>
                  <r.Icon className={`w-5 h-5 ${r.iconC}`} />
                </div>
                <div>
                  <div className="font-semibold text-zoku-ink">{r.t}</div>
                  <div className="text-sm text-zoku-ink2 max-w-sm">{r.d}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
